import { isFeatureEnabled } from 'owa-feature-flags';
import { isInViewport } from '../utils/isInViewPort';
import {
    mailListItemAnimationStore,
    clearAnimationStore,
    ActionId,
    setAnimationAction,
} from 'owa-mail-list-item-animation-store';
import { LoadMoreListViewHandle } from 'owa-loadmore-listview/lib/components/LoadMoreListView';
import { getIsDarkTheme } from 'owa-fabric-theme';

export const runListAnimation = (listView: React.MutableRefObject<LoadMoreListViewHandle>) => {
    if (
        isFeatureEnabled('mon-tri-listViewAnimation') &&
        mailListItemAnimationStore.animationRowIds?.size > 0
    ) {
        const prevChildrenPositions = new Map<string /* rowId */, DOMRect /* top offset */>(); // Create maps for the positions of elements
        const currentChildrenPositions = new Map<string /* rowId */, DOMRect /* offset */>();
        const rowItemParts = new Map</*ParentId*/ string, Element[] /* dom child element*/>();
        const animationAction = mailListItemAnimationStore.animationAction;

        const scrollRegion: HTMLDivElement = listView.current.getScrollRegion();

        const rows = Array.from(scrollRegion.children[0]?.children); // Get the scroll Region children and place them in an array
        if (rows) {
            rows.forEach(row => {
                // Store the old positions of the elements
                if ((row as HTMLDivElement).children.length == 2) {
                    // The second child of a mailRow is the itemParts
                    const itemPartChildren = Array.from(
                        (row as HTMLDivElement).children[1].children[0].children
                    );

                    itemPartChildren.forEach(itemPartRow => {
                        prevChildrenPositions.set(
                            itemPartRow.id,
                            itemPartRow.getBoundingClientRect()
                        );
                    });

                    rowItemParts.set(row.id, itemPartChildren);
                }
                prevChildrenPositions.set(row.id, row.getBoundingClientRect());
            });

            // When we absolutely position elements they stack on top of each other if there are not positioned items separating them.
            // We can replicate the original position by adding margin top to each item.
            if (
                animationAction != 'PinFinal' &&
                animationAction != 'UnpinFinal' &&
                animationAction != 'PinMiddle' &&
                animationAction != 'UnpinMiddle'
            ) {
                let removedElementMarginTop = 0;
                const cssText =
                    'position: absolute; z-index:0; width: inherit; background:' +
                    getAnimationBackgroundColor(mailListItemAnimationStore.animationAction) +
                    ';';

                const removeElement = (row: HTMLDivElement) => {
                    // absolute position the elements so we can take them out of the flow + find next position
                    if (mailListItemAnimationStore.animationRowIds.has(row.id)) {
                        row.style.cssText =
                            cssText + 'margin-top:' + removedElementMarginTop + 'px;';
                        removedElementMarginTop += prevChildrenPositions.get(row.id).height;
                    } else {
                        removedElementMarginTop = 0;
                    }
                };
                rows.forEach(row => {
                    // absolute position the elements so we can take them out of the flow + find next position

                    removeElement(row as HTMLDivElement);
                    rowItemParts.get(row.id)?.forEach(itemPart => {
                        removeElement(itemPart as HTMLDivElement);
                    });
                });
            }
            const isMiddlePinUnpin =
                animationAction == 'PinMiddle' || animationAction == 'UnpinMiddle';

            if (isMiddlePinUnpin) {
                mailListItemAnimationStore.onAnimationComplete();
            }

            const isFinalPinUnpin =
                animationAction == 'PinFinal' || animationAction == 'UnpinFinal';
            if (isFinalPinUnpin) {
                rows.forEach(row => {
                    if (mailListItemAnimationStore.animationRowIds.has(row.id)) {
                        (row as HTMLDivElement).style.cssText = `background: ${getAnimationBackgroundColor(
                            animationAction
                        )}`;
                    }
                });
            }

            rows.forEach(row => {
                // When we read the position, the dom snapshot will update, so we can get the desired "new" state with the item out of the flow
                currentChildrenPositions.set(row.id, row.getBoundingClientRect());
                rowItemParts.get(row.id)?.forEach(itemPart => {
                    currentChildrenPositions.set(itemPart.id, itemPart.getBoundingClientRect());
                });
            });

            let animatedItems = 0;
            const scrollRegionRect = scrollRegion.getBoundingClientRect();
            const parentRowChange = new Map<string /* rowId */, number /* top */>(); // store the parents directional change

            const animateRow = (row: Element, isItemPart: boolean = false) => {
                const isAnimatedRow = mailListItemAnimationStore.animationRowIds.has(row.id);
                if (
                    isInViewport(currentChildrenPositions.get(row.id), scrollRegionRect) ||
                    (isAnimatedRow && isFinalPinUnpin)
                ) {
                    // Check if the element is visible before animating
                    if (!isAnimatedRow) {
                        const firstBox = prevChildrenPositions.get(row.id).top;
                        const lastBox = currentChildrenPositions.get(row.id).top;
                        if (firstBox && lastBox) {
                            const changeY = firstBox - lastBox - (parentRowChange.get(row.id) ?? 0); // If an itemPart it needs to move separately
                            if (!isItemPart) {
                                parentRowChange.set(row.id, changeY);
                            }
                            const player = (row as HTMLDivElement).animate(
                                // TODO add other movements -- for pin and make this a helper function.
                                [
                                    { transform: 'translateY(' + changeY + 'px)', offset: 0 },
                                    { transform: 'translateY(' + changeY + 'px)', offset: 0.25 },
                                    { transform: 'translateY(0)' },
                                ],
                                {
                                    duration: isMiddlePinUnpin ? 10 : 433,
                                    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                                }
                            );
                            if (animatedItems == 0) {
                                player.onfinish = () => {
                                    handleAnimationComplete(animationAction, rows);
                                };
                                animatedItems = animatedItems + 1;
                            }
                        }
                    } else {
                        (row.children[0] as HTMLDivElement).animate(
                            getAnimationKeyframes(animationAction),
                            {
                                duration: isMiddlePinUnpin ? 10 : 333,
                                easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                                fill: 'forwards',
                            }
                        );
                    }
                }
            };
            rows.forEach(row => {
                animateRow(row);
                rowItemParts.get(row.id)?.forEach(itemPart => {
                    animateRow(itemPart, true /* isItemPart */);
                });
            });
        }
    }
};

function getAnimationBackgroundColor(actionId: ActionId) {
    const isDarkTheme = getIsDarkTheme();
    switch (actionId) {
        case 'Delete':
            return isDarkTheme ? '#442726' : '#FEF2F3';
        case 'Archive':
            return isDarkTheme ? '#393D1B' : '#DFF6DD';
        case 'Move':
            return isDarkTheme ? '#FED9CC' : '#FFF4CE';
        case 'PinInitial':
        case 'PinMiddle':
        case 'PinFinal':
            return isDarkTheme ? '#2B88D8' : '#EFF6FC';

        case '-':
        case 'None':
        case 'ReadUnread':
        case 'FlagUnflag':
        default:
            return 'transparent';
    }
}

function getAnimationKeyframes(actionId: ActionId) {
    switch (actionId) {
        case 'Delete':
            return [{ transform: 'translateX(-100%)', opacity: 0 }];
        case 'Archive':
            return [{ transform: 'translateX(-100%)', opacity: 0 }];
        case 'Move':
            return [{ transform: 'translateY(100%)', opacity: 0 }];

        case 'PinInitial':
            // Slide up from origin, fade out
            return [
                { transform: 'translateY(0) scale(100%)', transformOrigin: 'center', opacity: 1 },
                {
                    transform: 'translateY(-100%) scale(50%)',
                    transformOrigin: 'center',
                    opacity: 0,
                },
            ];
        case 'UnpinFinal':
            // Slide down from above landing at origin, fade in
            return [
                {
                    transform: 'translateY(-100%) scale(50%)',
                    transformOrigin: 'center',
                    opacity: 0,
                },
                { transform: 'translateY(0) scale(100%)', transformOrigin: 'center', opacity: 1 },
            ];

        case 'PinFinal':
            // Slide up from below landing at origin, fade in
            return [
                { transform: 'translateY(100%) scale(50%)', transformOrigin: 'center', opacity: 0 },
                { transform: 'translateY(0) scale(100%)', transformOrigin: 'center', opacity: 1 },
            ];
        case 'UnpinInitial':
            // Slide down from origin, fade out
            return [
                { transform: 'translateY(0) scale(100%)', opacity: 1 },
                { transform: 'translateY(100%) scale(50%)', opacity: 0 },
            ];

        case 'PinMiddle':
        case 'UnpinMiddle':
        case '-':
        case 'None':
        case 'ReadUnread':
        case 'FlagUnflag':
        default:
            return [];
    }
}

function getNextActionId(actionId: ActionId) {
    switch (actionId) {
        case 'PinInitial':
            return 'PinMiddle';
        case 'PinMiddle':
            return 'PinFinal';
        case 'UnpinInitial':
            return 'UnpinMiddle';
        case 'UnpinMiddle':
            return 'UnpinFinal';
    }

    return '-'; // Should never happen
}

function handleAnimationComplete(animationAction: ActionId, rows: Element[]) {
    if (animationAction == 'PinInitial' || animationAction == 'UnpinInitial') {
        rows.forEach(row => {
            if (mailListItemAnimationStore.animationRowIds.has(row.id)) {
                (row as HTMLDivElement).style.background = 'transparent';
            }
        });
        setAnimationAction(
            getNextActionId(animationAction),
            Array.from(mailListItemAnimationStore.animationRowIds),
            mailListItemAnimationStore.onAnimationComplete
        );
    } else if (animationAction == 'PinMiddle' || animationAction == 'UnpinMiddle') {
        setAnimationAction(
            getNextActionId(animationAction),
            Array.from(mailListItemAnimationStore.animationRowIds)
        );
    } else if (animationAction == 'PinFinal' || animationAction == 'UnpinFinal') {
        clearAnimationStore();
    } else {
        rows.forEach(row => {
            if (mailListItemAnimationStore.animationRowIds.has(row.id)) {
                (row as HTMLDivElement).style.opacity = '1';
            }
        });
        mailListItemAnimationStore.onAnimationComplete();
        clearAnimationStore();
    }
}
